import { useState } from "react";
import { ChevronDown, ChevronRight, Loader2, Send } from "lucide-react";
import Button3D from "@/components/orders/Button3D";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TEMPLATE_LABEL, describeProductLine } from "./templates";
import type { MailDraft } from "./types";

interface MailDraftEditorProps {
  drafts: MailDraft[];
  isSending: boolean;
  onDraftChange: (index: number, patch: Partial<Pick<MailDraft, "subject" | "body">>) => void;
  onBack: () => void;
  onSend: () => void;
}

const DecoprofWrapPreview = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-dashed bg-muted/30 p-2 text-[11px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 text-left text-muted-foreground hover:text-foreground"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <span className="font-medium">Huisstijl-tekst die om je bericht wordt gewikkeld</span>
      </button>
      {open && (
        <div className="mt-2 space-y-2 rounded bg-background p-3 text-[11px] leading-relaxed text-slate-700">
          <p className="italic text-muted-foreground">Beste [voornaam],</p>
          <p className="italic text-muted-foreground">Wij hebben nieuwe informatie over jouw bestelling:</p>
          <p className="rounded border border-primary/40 bg-primary/5 px-2 py-1 not-italic text-foreground">
            [hier komt jouw bericht]
          </p>
          <p className="italic text-muted-foreground">
            Mocht je nog vragen hebben dan kun je altijd contact met ons opnemen. Dit kan door te reageren op deze mail. Wil je ons liever persoonlijk spreken? Bel dan naar: 085-3037813.
          </p>
          <p className="italic text-muted-foreground">Hartelijk dank en succes!<br />Decoprof BV</p>
        </div>
      )}
    </div>
  );
};

export const MailDraftEditor = ({
  drafts,
  isSending,
  onDraftChange,
  onBack,
  onSend,
}: MailDraftEditorProps) => {
  const totalProducts = drafts.reduce((sum, draft) => sum + draft.productLines.length, 0);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/30 p-3">
        <div className="text-sm text-foreground">
          {drafts.length} {drafts.length === 1 ? "mail" : "mails"} voor {totalProducts}{" "}
          {totalProducts === 1 ? "product" : "producten"}
        </div>
      </div>

      <div className="mt-3 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
        {drafts.map((draft, index) => (
          <div
            key={`${draft.template_key}-${index}`}
            className="space-y-3 rounded-md border bg-background p-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="text-[10px]">
                {TEMPLATE_LABEL[draft.template_key]}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {draft.productLines.length}{" "}
                {draft.productLines.length === 1 ? "product" : "producten"}
              </span>
            </div>

            <ul className="space-y-1 pl-4 text-xs text-muted-foreground">
              {draft.productLines.map((productLine) => (
                <li key={productLine.id} className="list-disc">
                  {describeProductLine(productLine)}
                </li>
              ))}
            </ul>

            <div className="space-y-1.5">
              <Label htmlFor={`draft-subject-${index}`}>Onderwerp</Label>
              <Input
                id={`draft-subject-${index}`}
                value={draft.subject}
                onChange={(event) => onDraftChange(index, { subject: event.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={`draft-body-${index}`}>Bericht</Label>
              <Textarea
                id={`draft-body-${index}`}
                value={draft.body}
                placeholder="Schrijf hier je bericht"
                onChange={(event) => onDraftChange(index, { body: event.target.value })}
                className="min-h-[220px] text-xs"
              />
            </div>

            {draft.template_key === "leeg" && <DecoprofWrapPreview />}
          </div>
        ))}
      </div>

      <div className="mt-3 flex shrink-0 flex-wrap items-center justify-end gap-2 border-t bg-background pt-3">
        <Button3D tone="neutral" onClick={onBack} disabled={isSending}>
          Annuleren
        </Button3D>
        <Button3D tone="primary" onClick={onSend} disabled={isSending || drafts.length === 0}>
          {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Versturen naar testadres
        </Button3D>
      </div>
    </div>
  );
};
